import { useState, useRef, useEffect } from "react";
import { Typography, Tooltip } from "antd";
import { Pie } from "@ant-design/plots";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faChevronDown,
  faChevronUp,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./dashboard.module.scss";

const { Text } = Typography;

// Dark → light blue palette (index 0 = most spend, highest contrast)
const COLORS = ["#1a5fa8", "#2a80d4", "#4a9de0", "#7ab8e8", "#b0d5f0"];
const OTHER_COLOR = "#d9d9d9";

const formatCurrencyAbbr = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) {
    const kStr = (value / 1_000).toFixed(1);
    if (Number(kStr) >= 1_000) return `${(value / 1_000_000).toFixed(1)}M`;
    return `${kStr}K`;
  }
  return `${value.toFixed(2)}`;
};

const formatCurrencyFull = (value: number, skipDollar: boolean = false): string =>
  `${skipDollar? '' : '$'}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;


const SpendByTemplate: React.FC<any> = ({ spendData, dashboardData, filters }) => {
  const [otherExpanded, setOtherExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const otherListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (otherExpanded && scrollRef.current && otherListRef.current) {
      const halfHeight = otherListRef.current.offsetHeight / 2;
      scrollRef.current.scrollBy({ top: halfHeight, behavior: "smooth" });
    }
  }, [otherExpanded]);

  // Templates come in sorted by spend DESC from the API
  const templates: any[] = spendData?.templates ?? [];
  const totalSpend: number = dashboardData?.totalSpend?.value ?? 0;
  const previousSpend: number = dashboardData?.totalSpend?.previousPeriod ?? 0;

  const isNotCustom = !!filters?.[1];
  const percentageChange =
    previousSpend !== 0
      ? ((totalSpend - previousSpend) / previousSpend) * 100
      : 0;
  const isIncrease = percentageChange >= 0;

  const top5 = templates.slice(0, 5);
  const others = templates.slice(5);
  const otherTotal = others.reduce(
    (sum: number, t: any) => sum + (t.spend ?? 0),
    0
  );

  // Build a stable color map keyed by template name (ordered by spend DESC)
  const colorMap: Record<string, string> = {};
  top5.forEach((t: any, i: number) => {
    colorMap[t.templateName] = COLORS[i];
  });

  // Chart data: sorted ASCENDING so that stackY(reverse:true) places the
  // largest slice at startAngle = LEFT side of the arch
  // "Other" is shown in the list only — not as a pie slice
  const chartDataAsc = top5
    .slice()
    .sort((a: any, b: any) => (a.spend ?? 0) - (b.spend ?? 0))
    .map((t: any) => ({ type: t.templateName, value: t.spend ?? 0 }));
  const hasData = chartDataAsc.length > 0;

  // Explicit domain→range mapping so chart colors always match the list dots
  const scaleDomain = chartDataAsc.map((d) => d.type);
  const scaleRange = chartDataAsc.map((d) => colorMap[d.type] ?? OTHER_COLOR);

  const pieConfig: any = {
    data: hasData ? chartDataAsc : [{ type: "No Data", value: 1 }],
    angleField: "value",
    colorField: "type",
    scale: hasData
      ? { color: { domain: scaleDomain, range: scaleRange } }
      : { color: { range: ["#f0f0f0"] } },
    innerRadius: 0.65,
    startAngle: Math.PI,
    endAngle: 2 * Math.PI,
    legend: false,
    label: false,
    style: {
      radius: 10,       // outer arc corner rounding (px)
      innerRadius: 10,  // inner arc corner rounding (px)
      inset: 3,         // angular gap between slices
    },
    tooltip: hasData
      ? {
          items: [
            (d: any) => ({
              name: d.type,
              value: formatCurrencyFull(d.value),
              color: colorMap[d.type] ?? OTHER_COLOR,
            }),
          ],
        }
      : false,
    interactions: hasData ? [{ type: "element-active" }] : [],
    height: 380,
    width:300,
    display: 'inline',
    padding: 0,
  };

  const periodLabel = (() => {
    switch (filters?.[1]) {
      case "mtd":
        return "MTD";
      case "last30":
        return "LAST 30";
      case "lastMonth":
        return "PREV MONTH";
      case "ytd":
        return "YTD";
      default:
        return "";
    }
  })();

  return (
    <div className={styles.spendCard}>
      <Text strong className={styles.cardSectionTitle}>
        Total Spend by Template
      </Text>

      {/* Chart + center overlay */}
      <div className={styles.chartWrapper}>
        <Pie {...pieConfig}  style={{ display: 'inline-block' }}/>
        <div className={styles.chartCenter}>
          {isNotCustom && previousSpend > 0 && (
            <Text
              className={styles.spendTrend}
              style={{ color: isIncrease ? "#16D761" : "#f3083b" }}
            >
              <FontAwesomeIcon
                icon={isIncrease ? faArrowTrendUp : faArrowTrendDown}
                style={{ marginRight: 3 }}
              />
              {Math.abs(percentageChange).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}%
              <Tooltip
                title={`Previous period spend: ${formatCurrencyFull(previousSpend)}`}
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className={styles.cardIconSpend}
                />
              </Tooltip>
            </Text>
          )}
          <Text className={styles.spendTotal}>
            <span className={styles.dollar}>$</span>
            {formatCurrencyAbbr(totalSpend)}
          </Text>
          <Text type="secondary" className={styles.spendLabel}>
            TOTAL SPEND {periodLabel}
          </Text>
        </div>
      </div>

      {/* Two-column template table (no dividers) */}
      <div className={styles.templateList}>
        <div className={styles.templateRows} ref={scrollRef}>
        {top5.map((t: any) => (
          <div
            key={t.templateId ?? t.templateName}
            className={styles.templateRow}
          >
            <span className={styles.templateLabel}>
              <span
                className={styles.templateDot}
                style={{ backgroundColor: colorMap[t.templateName] }}
              />
              <Text className={styles.templateName}>{t.templateName}</Text>
            </span>
            <span>
              <Text className={styles.templateAmount}>
                <span className={styles.dollarVals}>$</span>
                {formatCurrencyFull(t.spend ?? 0, true)}
              </Text>
            </span>
          </div>
        ))}

        {otherTotal > 0 && (
          <>
            <div
              className={`${styles.templateRow} ${styles.otherToggleText} ${otherExpanded ? styles.otherExpandedBorder : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setOtherExpanded((prev) => !prev)}
            >
              <span className={styles.templateLabel}>
                <span
                  className={styles.templateDot}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #91CDF9",
                  }}
                />
                <Text className={styles.templateName}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span>Other</span>
                    <FontAwesomeIcon
                      icon={otherExpanded ? faChevronUp : faChevronDown}
                      style={{ fontSize: 10, color: '#99A0A7' }}
                    />
                  </span>
                </Text>
              </span>
              <span>
                <Text className={styles.templateAmount}>
                  <span className={styles.dollarVals}>$</span>
                  {formatCurrencyFull(otherTotal, true)}
                </Text>
              </span>
            </div>
            {otherExpanded && (
              <div className={styles.otherList} ref={otherListRef}>
                {others.map((t: any) => (
                  <div
                    key={t.templateId ?? t.templateName}
                    className={`${styles.templateRow} ${styles.templateRowOthers}`}
                  >
                    <span className={styles.templateLabel}>
                      <Text className={styles.templateName}>{t.templateName}</Text>
                    </span>
                    <span>
                      <Text className={styles.templateAmount}>
                        <span className={styles.dollarVals}>$</span>
                        {formatCurrencyFull(t.spend ?? 0, true)}
                      </Text>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default SpendByTemplate;
